/**
 * foot_camera — the on-foot FIRST↔THIRD person zoom and the hovercraft chase
 * zoom, both driven by the scroll wheel (2026-09-09, "have a 3rd person view
 * … switching by the scroll wheel like most games; zoom in/out when in a
 * vehicle so you can see where you are going").
 *
 * One number rules the foot camera: `camBack`, the distance the camera sits
 * behind the eye. 0 is first person (the view model shows, the body hides);
 * from FIRST_PERSON_MAX up it is third person: the camera orbits the HEAD —
 * rig-local (0, eye − d·sin(pitch), d·cos(pitch)) so the mouse pitch keeps the
 * head framed — and a ray from the head toward the camera shortens `d` when a
 * wall or the ground is in the way. Scrolling in past the threshold snaps to
 * exact first person; scrolling out from first person starts past the
 * inside-the-head band. Pure, so the harness pins every rule.
 */

export const FOOT_ZOOM_MAX = 8;          // m — the furthest third-person pull-back
export const FIRST_PERSON_MAX = 0.9;     // camBack below this = first person
export const FOOT_ZOOM_OUT_START = 1.6;  // the first notch out of first person lands here
export const FOOT_ZOOM_SENS = 0.006;     // m per wheel deltaY unit (≈ 0.6 m per notch)
export const CAMERA_PAD_M = 0.35;        // stand-off from a surface the camera ray hits
export const CAMERA_MIN_BACK_M = 0.6;    // never closer than this while third person is on

export const HOVER_ZOOM_MIN = 3;
export const HOVER_ZOOM_DEFAULT = 8.5;
export const HOVER_ZOOM_MAX = 45;
export const HOVER_ZOOM_SENS = 0.02;     // m per wheel deltaY unit (≈ 2 m per notch)
export const HOVER_RISE = 0.3;           // camera rise per metre of pull-back

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Is `camBack` a third-person distance? */
export function isThirdPerson(camBack: number): boolean { return camBack >= FIRST_PERSON_MAX; }

/**
 * Apply one wheel delta to the foot camBack. Scrolling OUT (deltaY > 0) from
 * first person jumps to FOOT_ZOOM_OUT_START; scrolling IN below the threshold
 * snaps to exact first person (0).
 */
export function footZoomStep(camBack: number, deltaY: number): number {
  if (!(Number.isFinite(deltaY)) || deltaY === 0) return camBack;
  if (deltaY > 0 && camBack < FIRST_PERSON_MAX) return FOOT_ZOOM_OUT_START;
  const next = clamp(camBack + deltaY * FOOT_ZOOM_SENS, 0, FOOT_ZOOM_MAX);
  return next < FIRST_PERSON_MAX ? 0 : next;
}

/** Rig-local camera position for a back distance and the mouse pitch (+ = look up). */
export function footCameraPose(camBack: number, pitch: number, eye: number): { x: number; y: number; z: number } {
  if (camBack <= 0) return { x: 0, y: eye, z: 0 };
  return { x: 0, y: eye - camBack * Math.sin(pitch), z: camBack * Math.cos(pitch) };
}

/** World-space unit direction from the head TOWARD the camera for a rig yaw + pitch. */
export function cameraRayDir(yaw: number, pitch: number): { x: number; y: number; z: number } {
  const cp = Math.cos(pitch);
  return { x: cp * Math.sin(yaw), y: -Math.sin(pitch), z: cp * Math.cos(yaw) };
}

/** Shorten the back distance to keep the camera CAMERA_PAD_M in front of a hit surface. */
export function collideCamBack(camBack: number, hitDist: number | null): number {
  if (hitDist === null || !(hitDist >= 0) || hitDist >= camBack + CAMERA_PAD_M) return camBack;
  return Math.max(CAMERA_MIN_BACK_M, hitDist - CAMERA_PAD_M);
}

/** Exponential approach of the drawn distance to the target (zoom feel). */
export function smoothCamBack(current: number, target: number, dt: number, rate = 14): number {
  const k = 1 - Math.exp(-Math.max(0, dt) * rate);
  return current + (target - current) * k;
}

/** Hovercraft chase: one wheel delta on the chase distance. */
export function hoverZoomStep(camBack: number, deltaY: number): number {
  if (!Number.isFinite(deltaY) || deltaY === 0) return camBack;
  return clamp(camBack + deltaY * HOVER_ZOOM_SENS, HOVER_ZOOM_MIN, HOVER_ZOOM_MAX);
}

/** World-space unit direction from the head toward the hovercraft chase camera (behind, rising HOVER_RISE per metre). */
export function hoverCameraRayDir(yaw: number): { x: number; y: number; z: number } {
  const n = Math.hypot(1, HOVER_RISE);
  const z = 1 / n, y = HOVER_RISE / n;
  return { x: z * Math.sin(yaw), y, z: z * Math.cos(yaw) };
}

/** Rig-local hovercraft chase camera: behind and rising with the distance (the mouse pitch still applies). */
export function hoverCameraPose(camBack: number, eye: number): { x: number; y: number; z: number } {
  return { x: 0, y: eye + HOVER_RISE * camBack, z: camBack };
}
